// Reports screen — Finance / Clients / Sessions analytics tabs.
import { useState, useEffect } from "react";
import { clientMonth, clientToday } from "../domain";
import { syncCol } from "../lib/sync";
import { cl, sl } from "../lib/format";
import { themeTokens } from "../lib/theme";
import { usePackageStates } from "../lib/packages";
import { Icon } from "../ui/primitives";

export const Reports=({clients,payments=[],appointments=[],attendanceRecords=[],theme,currency="₹",userId,profile})=>{
  const attendance=attendanceRecords; // the "attendance" collection, synced app-wide
  const [leads,setLeads]=useState([]);
  useEffect(()=>{
    if(!userId)return;
    syncCol(userId,"leads",setLeads); // the only collection App doesn't track
  },[userId]);
  const [tab,setTab]=useState("finance");
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);
  const p=theme?.primary||"#0f766e";

  const psOf=usePackageStates(clients);

  // Finance helpers
  const totalRev=payments.reduce((s,x)=>s+x.amount,0);
  const totalDue=clients.reduce((s,c)=>s+psOf(c).amountDue,0);
  const thisMonth=clientMonth();
  const _lm=new Date();_lm.setDate(1);_lm.setMonth(_lm.getMonth()-1);const lastMonth=clientMonth(_lm);
  const monthPay=payments.filter(x=>x.date?.startsWith(thisMonth));
  const lastMonthPay=payments.filter(x=>x.date?.startsWith(lastMonth));
  const monthRev=monthPay.reduce((s,x)=>s+x.amount,0);
  const lastMonthRev=lastMonthPay.reduce((s,x)=>s+x.amount,0);
  const revGrowth=lastMonthRev>0?Math.round(((monthRev-lastMonthRev)/lastMonthRev)*100):0;
  const byMode=["Cash","UPI","Bank Transfer","Cheque"].map(m=>({mode:m,total:payments.filter(x=>x.mode===m).reduce((s,x)=>s+x.amount,0)})).filter(x=>x.total>0);
  const byMonth={};payments.forEach(x=>{const m=x.date?.slice(0,7);if(m){byMonth[m]=(byMonth[m]||0)+x.amount;}});
  const monthEntries=Object.entries(byMonth).sort(([a],[b])=>b.localeCompare(a)).slice(0,6);
  const bestMonth=monthEntries.length>0?monthEntries.reduce((a,b)=>b[1]>a[1]?b:a):null;
  const collectionRate=clients.length>0?Math.round((clients.filter(c=>c.packagePrice>0&&(c.paid||0)>=(c.packagePrice||0)).length/clients.filter(c=>c.packagePrice>0).length)*100)||0:0;

  // Client helpers
  const activeClients=clients.filter(c=>c.package?.type!=="custom"||(c.classesRemaining||0)>0);
  const inactiveLeads=leads.filter(l=>l.source==="Existing Client");
  const newLeads=leads.filter(l=>l.source!=="Existing Client");
  const pkgBreakdown=["monthly","weekly","quarterly","yearly","custom"].map(t=>({type:t,count:clients.filter(c=>c.package?.type===t).length})).filter(x=>x.count>0);

  // Churn risk — clients with 0-1 sessions left or no attendance in 30 days
  const churnRisk=clients.filter(c=>{
    if(c.status==="inactive")return false;
    if((c.classesRemaining||0)<=1&&c.package?.type==="custom")return true;
    const lastAtt=attendance.filter(r=>r.clientId===c.id).sort((a,b)=>(b.date||'').localeCompare(a.date||''))[0]?.date;
    if(lastAtt){const days=(new Date(clientToday())-new Date(lastAtt))/(1000*60*60*24);if(days>30)return true;}
    return false;
  });

  // Appointment helpers
  const completed=appointments.filter(a=>a.status==="Completed").length;
  const cancelled=appointments.filter(a=>a.status==="Cancelled").length;
  const upcoming=appointments.filter(a=>a.status==="Upcoming").length;
  const totalSessions=attendance.length;
  const uniqueClients=new Set(attendance.map(a=>a.clientId)).size;
  const topAttendees=clients.map(c=>({name:c.name,count:c.classesAttended||0,total:c.package?.customClasses||c.package?.templateSessions||0})).sort((a,b)=>b.count-a.count).slice(0,5);
  const attendanceRate=completed+cancelled>0?Math.round((completed/(completed+cancelled))*100):0;

  const TabBtn=({id,label,icon})=>(
    <button onClick={()=>setTab(id)}
      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all"
      style={{background:tab===id?(isDark?"#0f172a":"#fff"):"transparent",color:tab===id?p:mc,boxShadow:tab===id?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
      <Icon name={icon} size={13}/>
      {label}
    </button>
  );

  const SummaryCard=({label,value,sub,color})=>(
    <div className="rounded-xl p-3 text-center border" style={{background:cb,borderColor:bc}}>
      <p className="text-2xl font-black" style={{fontVariantNumeric:"tabular-nums",color:color||p}}>{value}</p>
      <p className="text-xs font-semibold mt-0.5" style={{color:tc}}>{label}</p>
      {sub&&<p className="text-xs mt-0.5" style={{color:mc}}>{sub}</p>}
    </div>
  );

  return(
    <div className="space-y-4">
      <h1 className="text-2xl font-black" style={{fontFamily:"'Playfair Display',serif",color:tc}}>Reports</h1>

      {/* Tab selector */}
      <div className="flex gap-1 p-1 rounded-xl" style={{background:isDark?"#1e293b":"#f3f4f6"}}>
        <TabBtn id="finance" label="Finance" icon="payments"/>
        <TabBtn id="client" label={cl(profile,2)} icon="clients"/>
        <TabBtn id="appointment" label="Sessions" icon="appointments"/>
      </div>

      {/* ── FINANCE ── */}
      {tab==="finance"&&(
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard label="Total Revenue" value={`${currency}${totalRev.toLocaleString()}`} color="#16a34a"/>
            <SummaryCard label="This Month" value={`${currency}${monthRev.toLocaleString()}`} sub={revGrowth!==0?`${revGrowth>0?"↑":"↓"}${Math.abs(revGrowth)}% vs last month`:undefined} color={p}/>
            <SummaryCard label="Total Dues" value={`${currency}${totalDue.toLocaleString()}`} color="#dc2626"/>
            <SummaryCard label="Collection Rate" value={`${collectionRate}%`} sub="fully paid" color={collectionRate>=80?"#16a34a":"#d97706"}/>
          </div>
          {bestMonth&&(
            <div className="rounded-2xl p-4 border flex items-center gap-3" style={{background:isDark?"#0f172a":"#f0fdf4",borderColor:"#bbf7d0"}}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:"#16a34a"}}><Icon name="star" size={16} className="text-white"/></div>
              <div>
                <p className="text-sm font-bold" style={{color:"#16a34a"}}>Best Month</p>
                <p className="text-xs" style={{fontVariantNumeric:"tabular-nums",color:mc}}>{new Date(bestMonth[0]+"-01").toLocaleDateString("en-US",{month:"long",year:"numeric"})} — {currency}{bestMonth[1].toLocaleString()}</p>
              </div>
            </div>
          )}
          {/* Monthly breakdown */}
          {monthEntries.length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
              <p className="font-semibold text-sm mb-3" style={{color:tc}}>Monthly Income</p>
              {monthEntries.map(([m,amt])=>(
                <div key={m} className="flex items-center justify-between py-2" style={{borderTop:"1px dotted "+bc}}>
                  <span className="text-sm" style={{color:tc}}>{new Date(m+"-01").toLocaleDateString("en-US",{month:"long",year:"numeric"})}</span>
                  <span className="text-sm font-bold" style={{fontVariantNumeric:"tabular-nums",color:p}}>{currency}{amt.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
          {/* Payment mode breakdown */}
          {byMode.length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
              <p className="font-semibold text-sm mb-3" style={{color:tc}}>By Payment Mode</p>
              {byMode.map(({mode,total})=>(
                <div key={mode} className="flex items-center justify-between py-2" style={{borderTop:"1px dotted "+bc}}>
                  <span className="text-sm" style={{color:tc}}>{mode}</span>
                  <span className="text-sm font-bold" style={{fontVariantNumeric:"tabular-nums",color:p}}>{currency}{total.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
          {/* Per client dues */}
          <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
            <p className="font-semibold text-sm mb-3" style={{color:tc}}>Client-wise Dues</p>
            {clients.filter(c=>psOf(c).amountDue>0).length===0
              ?<p className="flex items-center gap-1.5 text-xs" style={{color:mc}}><Icon name="check" size={12}/>No pending dues</p>
              :clients.filter(c=>psOf(c).amountDue>0).map(c=>(
                <div key={c.id} className="flex items-center justify-between py-2" style={{borderTop:"1px dotted "+bc}}>
                  <span className="text-sm" style={{color:tc}}>{c.name}</span>
                  <span className="text-sm font-bold text-red-500" style={{fontVariantNumeric:"tabular-nums"}}>{currency}{psOf(c).amountDue}</span>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* ── CLIENTS ── */}
      {tab==="client"&&(
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard label={`Total ${cl(profile,2)}`} value={clients.length} color={p}/>
            <SummaryCard label="Active" value={activeClients.length} sub="with classes remaining" color="#16a34a"/>
            <SummaryCard label="Total Leads" value={leads.length} sub={`${newLeads.length} new`} color="#d97706"/>
            <SummaryCard label="Inactive" value={inactiveLeads.length} sub="moved to leads" color="#6b7280"/>
          </div>
          {/* Package breakdown */}
          {pkgBreakdown.length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
              <p className="font-semibold text-sm mb-3" style={{color:tc}}>Package Distribution</p>
              {pkgBreakdown.map(({type,count})=>(
                <div key={type} className="flex items-center gap-3 py-2 border-b last:border-0" style={{borderColor:bc}}>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm capitalize" style={{color:tc}}>{type}</span>
                      <span className="text-sm font-bold" style={{color:p}}>{count}</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{background:isDark?"#334155":"#e5e7eb"}}>
                      <div className="h-full rounded-full" style={{width:`${Math.round((count/clients.length)*100)}%`,background:p}}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Churn risk */}
          {churnRisk.length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:isDark?"#1e293b":"#fff7ed",borderColor:"#fed7aa"}}>
              <p className="flex items-center gap-1.5 font-bold text-sm mb-3" style={{color:"#ea580c"}}><Icon name="alert" size={13}/>At Risk — {churnRisk.length} {churnRisk.length===1?cl(profile):cl(profile,2)}</p>
              <p className="text-xs mb-3" style={{color:mc}}>Sessions running low or no attendance in 30+ days</p>
              {churnRisk.slice(0,5).map(c=>(
                <div key={c.id} className="flex items-center justify-between py-1.5 border-b last:border-0" style={{borderColor:isDark?"#334155":"#fed7aa"}}>
                  <span className="text-sm" style={{color:tc}}>{c.name}</span>
                  <span className="text-xs font-semibold" style={{color:"#ea580c"}}>{(c.classesRemaining||0)<=1?"Last session":"30+ days idle"}</span>
                </div>
              ))}
            </div>
          )}
          {/* Expiring soon */}
          {clients.filter(c=>c.classesRemaining===1).length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:"#fffbeb",borderColor:"#fde68a"}}>
              <p className="flex items-center gap-1.5 font-bold text-sm mb-3 text-amber-700"><Icon name="alert" size={13}/>Renewal Needed</p>
              {clients.filter(c=>c.classesRemaining===1).map(c=>(
                <p key={c.id} className="text-sm py-1 text-amber-700">{c.name} — 1 class left</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── APPOINTMENTS ── */}
      {tab==="appointment"&&(
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard label={`Total ${sl(profile,2)}`} value={totalSessions} sub="attendance marked" color={p}/>
            <SummaryCard label="Attendance Rate" value={`${attendanceRate}%`} sub="completed vs cancelled" color={attendanceRate>=80?"#16a34a":"#d97706"}/>
            <SummaryCard label="Completed" value={completed} sub="appointments" color="#16a34a"/>
            <SummaryCard label="Cancelled" value={cancelled} sub="appointments" color="#dc2626"/>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <SummaryCard label="Upcoming" value={upcoming} sub="scheduled appointments" color="#1d4ed8"/>
          </div>
          {/* Top attendees */}
          {topAttendees.filter(x=>x.count>0).length>0&&(
            <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
              <p className="font-semibold text-sm mb-3" style={{color:tc}}>{`Most Active ${cl(profile,2)}`}</p>
              {topAttendees.filter(x=>x.count>0).map((c,i)=>(
                <div key={c.name} className="flex items-center gap-3 py-2 border-b last:border-0" style={{borderColor:bc}}>
                  <span className="text-sm font-bold w-5" style={{color:mc}}>{i+1}</span>
                  <span className="flex-1 text-sm" style={{color:tc}}>{c.name}</span>
                  <span className="text-sm font-bold" style={{color:p}}>{c.count} sessions</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
